/**
 * alertsApi.ts - endpoints liés aux notifications/alertes.
 */
import { apiFetch } from './apiClient';

export interface TestEmailResponse {
  sent:       boolean;
  recipient:  string;
  testId:     string;
  sentAt:     string;
  message:    string;
}

export type AlertRuleType = 'scoreRegression' | 'criticalFail' | 'errorSpike' | 'weeklyDigest';
export type AlertFrequency = 'on-scan' | 'daily' | 'weekly' | 'monthly';

export interface AlertRule {
  id:                   string;
  clientId:             string;
  type:                 AlertRuleType;
  enabled:              boolean;
  recipients:           string[];
  /** Seuil numerique selon le type (points pour regression, count pour errorSpike). */
  thresholdPoints?:     number | null;
  frequency:            AlertFrequency;
  createdAt?:           string | null;
  updatedAt?:           string;
  updatedBy?:           string;
  lastTriggeredAt?:     string | null;
  lastTriggeredScanId?: string | null;
}

export interface AlertHistoryItem {
  id:               string;
  clientId:         string;
  ruleId:           string;
  ruleType:         AlertRuleType;
  triggeredAt:      string;
  scanId?:          string;
  recipients:       string[];
  subject?:         string;
  deliveryStatus:   'sent' | 'failed' | 'partial';
  deliveryError?:   string | null;
  details?:         Record<string, unknown>;
}

export const alertsApi = {
  /**
   * Envoie un email de test - utile pour valider que la chaine ACS marche
   * et que le destinataire peut bien recevoir des alertes futures.
   * @param email Si vide ou non fourni, le backend utilise l email de session.
   */
  testEmail: (email?: string) =>
    apiFetch<TestEmailResponse>('/alerts/test-email', {
      method: 'POST',
      body:   JSON.stringify(email ? { email } : {}),
      headers: { 'Content-Type': 'application/json' },
    }),

  /** Liste les règles d alerte du tenant (avec defaults si vide). */
  listRules: () => apiFetch<AlertRule[]>('/alerts/rules'),

  /** Upsert d une règle (creates if id new, updates sinon). */
  upsertRule: (rule: Partial<AlertRule> & { type: AlertRuleType }) =>
    apiFetch<AlertRule>('/alerts/rules', {
      method: 'POST',
      body:   JSON.stringify(rule),
    }),

  /** Historique des alertes déclenchées (50 dernières par défaut). */
  listHistory: (limit = 50) =>
    apiFetch<{ items: AlertHistoryItem[]; total: number }>(`/alerts/history?limit=${limit}`),

  /** Envoie un email de TEST avec contenu factice pour valider la config d une règle. */
  testRule: (ruleType: AlertRuleType, recipients?: string[]) =>
    apiFetch<{ ruleType: AlertRuleType; recipients: string[]; sentAt: string; sentBy: string; message: string }>(
      '/alerts/test',
      {
        method: 'POST',
        body:   JSON.stringify({ ruleType, recipients }),
        headers: { 'Content-Type': 'application/json' },
      },
    ),
};
